import { Grid } from "@material-ui/core";
import { noop } from "lodash";
import { FunctionComponent, useContext, useEffect, useState } from "react";
import { from } from "rxjs";
import { map, mergeAll } from "rxjs/operators";
import { Option } from "space-monad";
import styled from "styled-components";
import { Image } from "../../../../../../components/image";
import { AllImages } from "../../../../../../components/image/all-images-context";
import {
  BORDER_SIZE,
  IMAGE_SIZE,
  PADDING_SIZE,
  useKycImage,
} from "../../../../../../components/image/common";
import { Attachment, countries } from "../../../../../../store/common/models";
import { VerificationDTO } from "../../../../../../store/verification/models/dto/due-diligence";
import { VerificationService } from "../../../../../../store/verification/service";
import { StyledWidget } from "../common";

interface IKYBProps {
  item: Exclude<
    VerificationDTO.Responses.DueDiligence.Form.Get.T["kyb"],
    null | undefined
  >;
  dueDiligenceId: VerificationDTO.Responses.DueDiligence.Form.Get.T["id"];
  attachments: VerificationDTO.Responses.DueDiligence.Form.Get.T["attachments"];
}

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    ${IMAGE_SIZE + PADDING_SIZE + BORDER_SIZE}px
  );
  grid-gap: 20px;
  flex-grow: 1;
`;

const KYB: FunctionComponent<IKYBProps> = ({
  item,
  attachments,
  dueDiligenceId,
}) => {
  const { setFiles, files } = useContext(AllImages);

  useEffect(() => {
    if (item.details?.fraudCheckId) {
      const sub = from(
        attachments.filter(
          (x) =>
            x.type === "COMPANY_STRUCTURE_CHART" ||
            x.type === "INCUBATION_OR_INCORPORATION"
        )
      )
        .pipe(
          map((image) =>
            VerificationService.getDueDiligenceDocument({
              docId: image.id,
              dueDiligenceId,
            }).then((o) => o.map((x) => ({ file: x, ...image })))
          ),
          mergeAll()
        )
        .subscribe((option) => {
          option.fold(noop, (x) => {
            setFiles((y) => y.filter((z) => z.id !== x.id).concat([x]));
          });
        });
      return () => {
        setFiles([]);
        sub.unsubscribe();
      };
    }
  }, [attachments, item]);

  return (
    <StyledWidget title="Company Details">
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Grid container spacing={3}>
            <Grid item xs={6} lg={3}>
              Estimated annual turnover
            </Grid>
            <Grid item xs={6} lg={3}>
              {item.estimatedAnnualTurnover}
            </Grid>
            <Grid item xs={6} lg={3}>
              Tax Country
            </Grid>
            <Grid item xs={6} lg={3}>
              {countries.enum[item.taxCountry]}
            </Grid>
            <Grid item xs={6} lg={3}>
              Tax identification no
            </Grid>
            <Grid item xs={6} lg={3}>
              {item.taxIdentificationNo}
            </Grid>
            <Grid item xs={6} lg={3}>
              Whitepaper URL
            </Grid>
            <Grid item xs={6} lg={3}>
              <a
                href={item.whitepaperUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {item.whitepaperUrl}
              </a>
            </Grid>
            <Grid item xs={6} lg={3}>
              Tokenomics URL
            </Grid>
            <Grid item xs={6} lg={3}>
              <a
                href={item.tokenomicsUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {item.tokenomicsUrl}
              </a>
            </Grid>
          </Grid>
        </Grid>

        {Option(files.find((x) => x.type === "COMPANY_STRUCTURE_CHART")).fold(
          () => null,
          (props) => (
            <Grid item xs={12} md={6} lg={3}>
              <strong style={{ display: "block", marginBottom: ".5em" }}>
                Company Structure
              </strong>
              <ImageGrid>
                <Image {...props} />
              </ImageGrid>
            </Grid>
          )
        )}

        {Option(
          files.find((x) => x.type === "INCUBATION_OR_INCORPORATION")
        ).fold(
          () => null,
          (props) => (
            <Grid item xs={12} md={6} lg={3}>
              <strong style={{ display: "block", marginBottom: ".5em" }}>
                Incubation / Incorporation
              </strong>
              <ImageGrid>
                <Image {...props} />
              </ImageGrid>
            </Grid>
          )
        )}
      </Grid>
    </StyledWidget>
  );
};

export default KYB;
